import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UseMutationResult } from 'react-query';
import { useSelector } from 'react-redux';

import { OrderDeclineType } from 'types/enums/OrderDeclineType';
import { OrderStatus } from 'types/enums/OrderStatus';

import {
  OneClickPaymentResponse,
  OneClickRecurringPayload,
  OneClickResignPayload,
} from 'api/PaymentApi';
import { getDeclineTypeTestCookie } from 'helpers/payment/helpers';
import { usePaymentCardQuery } from 'hooks/usePaymentCardQuery';
import { useToast } from 'hooks/useToast';
import { isFreeVipEnabledSelector } from 'store/common/selectors';

import { WebSocketContext } from 'components/shared/WebSocketProvider';

type PaymentCallback = (data: OneClickPaymentResponse) => void;

// TODO make all callbacks required
interface MutationCallbacks {
  onProcessPayment?: PaymentCallback;
  onSuccessPayment?: PaymentCallback;
  onInsufficientFundsPaymentFail?: PaymentCallback;
  onRetryablePaymentFail?: PaymentCallback;
  on3dsVerifyPaymentFail?: PaymentCallback;
  onFinalPaymentFail?: PaymentCallback;
  onResubscribePaymentFail?: PaymentCallback;
}

interface RecurrentMutationPayload extends MutationCallbacks {
  packageId: string;
  isRefilled?: boolean;
}

interface ResignMutationPayload extends MutationCallbacks {
  packageId: string;
  cvv: string;
  isRefilled?: boolean;
}

interface Props {
  recurrentMutation: UseMutationResult<
    OneClickPaymentResponse,
    unknown,
    OneClickRecurringPayload
  >;
  resignMutation: UseMutationResult<
    OneClickPaymentResponse,
    unknown,
    OneClickResignPayload
  >;
  isEnabledResubscription?: boolean;
}

export const useOneClickPayment = ({
  recurrentMutation,
  resignMutation,
  isEnabledResubscription = false,
}: Props) => {
  const { paymentOrderSubscriptions } = useContext(WebSocketContext);

  const [status, setStatus] = useState<OrderStatus | null>(null);

  const onProcessPaymentCb = useRef<PaymentCallback | null>(null);
  const onSuccessPaymentCb = useRef<PaymentCallback | null>(null);
  const onInsufficientFundsPaymentFailCb = useRef<PaymentCallback | null>(null);
  const onRetryablePaymentFailCb = useRef<PaymentCallback | null>();
  const on3dsVerifyPaymentFailCb = useRef<PaymentCallback | null>(null);
  const onFinalPaymentFailCb = useRef<PaymentCallback | null>(null);
  const onResubscribePaymentFailCb = useRef<PaymentCallback | null>(null);
  const isOrderApplied = useRef(false);

  const isFreeVipEnabled = useSelector(isFreeVipEnabledSelector);

  const { showErrorToast } = useToast();

  const { paymentCardQuery } = usePaymentCardQuery({
    onError: () => {
      showErrorToast({
        title: 'Something went wrong',
        subtitle: 'Please, try again later',
      });
    },
  });

  const isCardTokenLoading = useMemo(
    () => paymentCardQuery.isFetching,
    [paymentCardQuery.isFetching]
  );

  const cardMask = paymentCardQuery.data?.card?.mask;

  const handlePaymentRequestFail = () => {
    setStatus(OrderStatus.Error);
  };

  const setCallbacks = (callbacks: MutationCallbacks) => {
    onProcessPaymentCb.current = callbacks.onProcessPayment || null;
    onSuccessPaymentCb.current = callbacks.onSuccessPayment || null;
    onInsufficientFundsPaymentFailCb.current =
      callbacks.onInsufficientFundsPaymentFail || null;
    onRetryablePaymentFailCb.current = callbacks.onRetryablePaymentFail || null;
    on3dsVerifyPaymentFailCb.current = callbacks.on3dsVerifyPaymentFail || null;
    onFinalPaymentFailCb.current = callbacks.onFinalPaymentFail || null;
    onResubscribePaymentFailCb.current =
      callbacks.onResubscribePaymentFail || null;
  };

  // ! isOrderApplied.current = true
  // this used for ignoring error message after success message from socket

  const resolveSocketPaymentStatus = useCallback(
    ({ data }: { data: OneClickPaymentResponse }) => {
      if (
        data.order === null &&
        isEnabledResubscription &&
        !isFreeVipEnabled &&
        !isOrderApplied.current
      ) {
        onResubscribePaymentFailCb.current &&
          onResubscribePaymentFailCb.current(data);
        return;
      }

      const orderStatus = data.order.status;
      const declineType =
        (orderStatus === OrderStatus.Error && getDeclineTypeTestCookie()) ||
        data.order.decline_type;
      const isInsufficientFundsError =
        declineType === OrderDeclineType.InsufficientFunds;
      const isRetrableError = declineType === OrderDeclineType.Retryable;
      const isFinalError =
        declineType === OrderDeclineType.Final ||
        declineType === OrderDeclineType.MethodFinal;

      if (orderStatus === OrderStatus.Process) {
        setStatus(OrderStatus.Process);
        onProcessPaymentCb.current && onProcessPaymentCb.current(data);
      }

      if (orderStatus === OrderStatus.Success) {
        isOrderApplied.current = true;
        setStatus(OrderStatus.Success);
        onSuccessPaymentCb.current && onSuccessPaymentCb.current(data);
      }

      if (orderStatus === OrderStatus.Error) {
        setStatus(OrderStatus.Error);

        if (
          !isFreeVipEnabled &&
          !isOrderApplied.current &&
          isEnabledResubscription
        ) {
          onResubscribePaymentFailCb.current &&
            onResubscribePaymentFailCb.current(data);
          return;
        }

        if (data.verify_url) {
          on3dsVerifyPaymentFailCb.current &&
            on3dsVerifyPaymentFailCb.current(data);
        }

        if (isInsufficientFundsError) {
          onInsufficientFundsPaymentFailCb.current &&
            onInsufficientFundsPaymentFailCb.current(data);
        }
        if (isRetrableError) {
          onRetryablePaymentFailCb.current &&
            onRetryablePaymentFailCb.current(data);
        }
        if (isFinalError) {
          onFinalPaymentFailCb.current && onFinalPaymentFailCb.current(data);
        }
      }
    },
    [isEnabledResubscription, isFreeVipEnabled]
  );

  const handleRecurrentSubmit = ({
    packageId,
    isRefilled,
    ...callbacks
  }: RecurrentMutationPayload) => {
    if (paymentCardQuery.isSuccess) {
      const cardToken = paymentCardQuery.data.card.id;
      setCallbacks(callbacks);
      setStatus(OrderStatus.Process);

      recurrentMutation.mutate(
        {
          cardToken,
          packId: packageId,
          isRefilled,
        },
        {
          onError: handlePaymentRequestFail,
        }
      );
    }
  };

  const handleResignSubmit = ({
    packageId,
    cvv,
    isRefilled,
    ...callbacks
  }: ResignMutationPayload) => {
    if (paymentCardQuery.isSuccess) {
      const cardToken = paymentCardQuery.data.card.id;
      setCallbacks(callbacks);
      setStatus(OrderStatus.Process);

      resignMutation.mutate(
        {
          cardToken,
          packId: packageId,
          cvv,
          isRefilled,
        },
        {
          onError: handlePaymentRequestFail,
        }
      );
    }
  };

  useEffect(() => {
    const subscription = paymentOrderSubscriptions?.on(
      'publication',
      resolveSocketPaymentStatus
    );

    return () => {
      subscription?.removeListener('publication', resolveSocketPaymentStatus);
    };
  }, [paymentOrderSubscriptions, resolveSocketPaymentStatus]);

  return {
    cardMask,
    status,
    isSuccess: status === OrderStatus.Success,
    isError: status === OrderStatus.Error,
    isProcess: status === OrderStatus.Process,
    isCardTokenLoading,
    handleRecurrentSubmit,
    handleResignSubmit,
  };
};
